export type LogLineSource = 'event' | 'durable' | 'log';

export const LOG_LINE_SOURCE_LABELS: Record<LogLineSource, string> = {
  event: 'Engine',
  durable: 'Durable',
  log: 'Log',
};

const DATE_FORMAT_OPTIONS: Intl.DateTimeFormatOptions = {
  year: 'numeric',
  month: '2-digit',
  day: '2-digit',
  hour: '2-digit',
  minute: '2-digit',
  second: '2-digit',
  hour12: false,
};

// The 'sv' locale gives an ISO-like local date. Milliseconds are appended by
// hand because `fractionalSecondDigits` is outside the configured TS lib.
export const formatAbsoluteTimestamp = (timestamp: string): string => {
  const date = new Date(timestamp);
  const milliseconds = String(date.getMilliseconds()).padStart(3, '0');
  const seconds = date
    .toLocaleString('sv', DATE_FORMAT_OPTIONS)
    .replace(',', '.');
  return `${seconds}.${milliseconds}`;
};

export type TimelineEntry = {
  id?: string;
  timestamp?: string;
};

export type TimelineTime = {
  ms: number;
  subMsNanos: number;
};

// Engine timestamps carry microseconds, but Date only keeps milliseconds and
// engines disagree on truncating versus rounding the rest. The extra digits are
// cut before parsing and kept apart so events within the same millisecond
// still order correctly.
const SUB_MS_FRACTION = /(T\d{2}:\d{2}:\d{2}\.\d{3})(\d+)/;
const SUB_MS_DIGITS = 6;

export const parseTimelineTime = (
  timestamp: string | undefined,
): TimelineTime | undefined => {
  if (!timestamp) {
    return undefined;
  }

  const match = SUB_MS_FRACTION.exec(timestamp);
  const ms = Date.parse(
    match ? timestamp.replace(match[0], match[1]) : timestamp,
  );
  if (!Number.isFinite(ms)) {
    return undefined;
  }

  const subMsDigits = (match?.[2] ?? '')
    .slice(0, SUB_MS_DIGITS)
    .padEnd(SUB_MS_DIGITS, '0');
  return { ms, subMsNanos: Number(subMsDigits) };
};

export const compareTimelineTimes = (
  left: TimelineTime,
  right: TimelineTime,
): number => left.ms - right.ms || left.subMsNanos - right.subMsNanos;

// Chronological comparison of raw timestamps: negative when `left` is earlier.
// Returns undefined when either side does not parse, so a caller deciding a
// cutoff must choose explicitly what an unparseable timestamp means.
export const compareTimestamps = (
  left: string | undefined,
  right: string | undefined,
): number | undefined => {
  const leftTime = parseTimelineTime(left);
  const rightTime = parseTimelineTime(right);
  if (!leftTime || !rightTime) {
    return undefined;
  }
  return compareTimelineTimes(leftTime, rightTime);
};

export const getTimelineDeltaMs = (
  later: TimelineTime,
  earlier: TimelineTime,
): number =>
  later.ms - earlier.ms + (later.subMsNanos - earlier.subMsNanos) / 1e6;

// A missing id compares as '' to keep the comparator transitive; rows without
// an id still keep their input order among themselves (Array.sort is stable).
const compareIds = (left: string | undefined, right: string | undefined) =>
  (left ?? '').localeCompare(right ?? '');

// Newest first, the exact reverse of the chronological order used for step
// deltas, so each delta describes the gap to the row right below it. Rows
// without a valid timestamp go last in their input order.
export const sortNewestFirst = <TEntry extends TimelineEntry>(
  entries: TEntry[],
): TEntry[] => {
  const timed = entries.map((entry) => ({
    entry,
    time: parseTimelineTime(entry.timestamp),
  }));

  timed.sort((left, right) => {
    if (!left.time || !right.time) {
      return Number(!left.time) - Number(!right.time);
    }
    return (
      compareTimelineTimes(right.time, left.time) ||
      compareIds(right.entry.id, left.entry.id)
    );
  });

  return timed.map(({ entry }) => entry);
};
